import { HelpCircle, Mail, MessageSquare, Sparkles, X } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FeatureWall } from './FeatureWall';
import { FeedbackModal } from './FeedbackModal';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || '';
  
  const [activeView, setActiveView] = useState<'menu' | 'feedback' | 'features'>('menu');

  if (!isOpen) return null;

  // Sub-modals take over the view or render on top?
  // Let's render them on top if active, or switch view.
  // Switching view keeps it in one modal context which is cleaner for mobile.
  
  if (activeView === 'feedback') {
      return <FeedbackModal isOpen={true} onClose={() => setActiveView('menu')} userId={userId} />;
  }

  if (activeView === 'features') {
      return <FeatureWall isOpen={true} onClose={() => setActiveView('menu')} userId={userId} />;
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white">
           <h3 className="font-display font-extrabold text-xl text-brand-dark flex items-center gap-2">
               <HelpCircle size={24} className="text-brand-red" />
               Support & Feedback
           </h3>
           <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto bg-brand-cream/30">
            
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
                <button 
                    onClick={() => setActiveView('feedback')}
                    className="p-4 bg-white border-2 border-brand-cream-dark rounded-2xl flex flex-col items-center gap-2 hover:border-brand-dark transition-all hover:shadow-md group"
                >
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <MessageSquare size={24} />
                    </div>
                    <span className="font-bold text-brand-dark text-sm">Send Feedback</span>
                </button>

                <button 
                    onClick={() => setActiveView('features')}
                    className="p-4 bg-white border-2 border-brand-cream-dark rounded-2xl flex flex-col items-center gap-2 hover:border-brand-dark transition-all hover:shadow-md group"
                >
                    <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Sparkles size={24} />
                    </div>
                    <span className="font-bold text-brand-dark text-sm">Feature Wall</span>
                </button>
            </div>

            {/* Contact */}
            <div className="bg-white p-5 rounded-2xl border border-brand-cream-dark shadow-sm">
                <h4 className="font-display font-extrabold text-brand-dark mb-2">Need help?</h4>
                <p className="text-sm text-brand-dark-light mb-4">
                    Found a bug or just want to say hi? Drop us an email directly.
                </p>
                <a 
                    href={`mailto:hello@luke2launch.com?subject=Good%20Shout%20Support&body=App%20Version:%20${__APP_VERSION__}`}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-brand-dark text-white rounded-xl font-bold hover:bg-brand-dark/90 transition"
                >
                    <Mail size={18} />
                    Email Support
                </a>
            </div>

            {/* FAQs */}
            <div className="space-y-3">
                <h4 className="font-display font-extrabold text-brand-dark px-1">FAQs</h4>
                
                <details className="bg-white rounded-xl border border-brand-cream-dark overflow-hidden group">
                    <summary className="p-4 font-bold text-sm text-brand-dark cursor-pointer flex justify-between items-center list-none">
                        How do I add a recommendation?
                        <span className="transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-brand-dark-light">
                        Click the big red "+" button in the bottom right corner. You can search for a place or drop a pin manually.
                    </div>
                </details>

                <details className="bg-white rounded-xl border border-brand-cream-dark overflow-hidden group">
                    <summary className="p-4 font-bold text-sm text-brand-dark cursor-pointer flex justify-between items-center list-none">
                        Who can see my pins?
                        <span className="transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-brand-dark-light">
                        Only your friends! Good Shout is about trusted recommendations, not public noise.
                    </div>
                </details>

                <details className="bg-white rounded-xl border border-brand-cream-dark overflow-hidden group">
                    <summary className="p-4 font-bold text-sm text-brand-dark cursor-pointer flex justify-between items-center list-none">
                        How do I install the app?
                        <span className="transition-transform group-open:rotate-180">▼</span>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-brand-dark-light">
                        On mobile, tap "Share" then "Add to Home Screen" (iOS) or "Install App" (Android).
                    </div>
                </details>
            </div>

        </div>
      </div>
    </div>
  );
};
